<!-- eslint-disable @typescript-eslint/no-explicit-any -->
<script setup lang="ts">
import MiiaFwAuthModal from './AuthModal.vue'
import MiiaFwAlertMessageLight from './AlertMessageLight.vue'
import MiiaFwPasswordReset from './PasswordReset.vue'
import MiiaFwPasswordInitialize from './PasswordInitialize.vue'
import MiiaFwPasswordUpdate from './PasswordUpdate.vue'
import { useLoadingView } from '../composables/useLoadingView'
import { signin } from '../utils/miiaFwCognito'
import MiiaFwConfig from '@/configs/config'
import MiiaFwAuthChallenge from './AuthChallenge.vue'
import { reactive, ref, useTemplateRef } from 'vue'
import type { AuthInfo } from '../types/cognito/auth'
import { isMiiaFwError } from '../types/error'

/**
 * @module components/miiafw/auth/MiiaFwSignin
 * @description 「サインイン」画面の機能
 * @vue-data {Boolean} [modalTop = false] 最初に開くモ―ダルの制御。trueのときモーダルを開く
 * @vue-data {Boolean} [myModal = false] モーダル制御。trueのときモーダルを開く
 * @vue-data {String} [userName = 空白] モーダル内に表示させるユーザID
 * @vue-data {String} [password = 空白] パスワード
 */
const passwordResetLink = useTemplateRef<HTMLAnchorElement>('passwordResetLink')
const userNameInput = useTemplateRef<HTMLInputElement>('userNameInput')
const passwordUpdate = useTemplateRef<InstanceType<typeof MiiaFwPasswordUpdate>>('passwordUpdate')
const passwordReset = useTemplateRef<InstanceType<typeof MiiaFwPasswordReset>>('passwordReset')
const passwordInitialize =
  useTemplateRef<InstanceType<typeof MiiaFwPasswordInitialize>>('passwordInitialize')
const alertMessageLight =
  useTemplateRef<InstanceType<typeof MiiaFwAlertMessageLight>>('alertMessageLight')
const authChallenge = useTemplateRef<InstanceType<typeof MiiaFwAuthChallenge>>('authChallenge')

const { setLoadingView } = useLoadingView()

const modalTop = ref(false)
const myModal = ref(false)
const userName = ref('')
const password = ref('')
const alertView = reactive({
  un: { hasError: false, alertMessage: '' },
  pw: { hasError: false, alertMessage: '' }
})
const passwordInput = ref<HTMLInputElement>(null as any)

/**
 * モーダルを開く
 */
const openModal = (): void => {
  modalTop.value = true
  myModal.value = true
}

/**
 * ユーザIDが入った状態のモーダルを開く。
 * @param argUserName ユーザIDが定義済みのユーザIDならば引数に入れて、モーダルに表示する
 */
const openMyModal = (argUserName: string): void => {
  if (argUserName !== undefined && argUserName.length > 0) {
    userName.value = argUserName
  }
  myModal.value = true
}

/** エラーメッセージを初期化 */
const resetAlertView = () => {
  Object.keys(alertView).forEach(key => {
    const view = alertView[key as 'un' | 'pw']
    view.hasError = false
    view.alertMessage = ''
  })
}

/**
 * モーダル内のユーザID、パスワード、エラー表示を消去した後、モーダルを閉じる。
 */
const closeMyModal = (): void => {
  userName.value = ''
  password.value = ''
  resetAlertView()
  myModal.value = false
}

/**
 * 一番上のモーダルを閉じる。＜MiiaFwAuthModal＞タグを指定することで、特定の操作に対応付けることができる。
 */
const closeModal = (): void => {
  closeMyModal()
  modalTop.value = false
}

/**
 * モーダル上でTab操作をした際、モーダル上のオブジェクトを循環できるようにする。
 * @param e キーボードイベント
 */
const firstTab = (e: KeyboardEvent): void => {
  e.preventDefault()
  e.stopPropagation()
  passwordResetLink.value?.focus()
}

/**
 * モーダル上でShift+Tab操作をした際、モーダル上のオブジェクトを循環できるようにする
 * @param e キーボードイベント
 */
const lastTab = (e: KeyboardEvent): void => {
  // Shift + Tabは、Tabの遡りなのでデフォルトイベントのまま
  if (!e.shiftKey) {
    e.preventDefault()
    e.stopPropagation()
    userNameInput.value?.focus()
  }
}

/**
 * バリデーションチェックを行った後、サインイン処理を呼び出す。表示されているエラーを初期化し、各項目に対してバリデーションチェックを行う。問題箇所があったらエラーを表示する。問題が無かったらmodule:modules/miiafw/MiiaFwCognito#signinへユーザIDとパスワードを渡し、モーダルを閉じる。
 */
const doSubmit = async (): Promise<void> => {
  // エラー表示初期化
  resetAlertView()
  alertMessageLight.value?.deleteAlertMessage()
  // バリデーションチェック
  if (userName.value.length < 1) {
    alertView.un.hasError = true
    alertView.un.alertMessage = 'ユーザーIDは必須項目です'
  }
  if (password.value.length < 1) {
    alertView.pw.hasError = true
    alertView.pw.alertMessage = 'パスワードは必須項目です'
  }
  if (!alertView.un.hasError && !alertView.pw.hasError) {
    // 処理中表示
    setLoadingView(true)
    // サインイン処理
    try {
      const result = await signin(userName.value, password.value)
      if (
        Object.prototype.hasOwnProperty.call(result, 'needMfa') &&
        (result as AuthInfo).needMfa &&
        Object.prototype.hasOwnProperty.call(result, 'challengeName') &&
        Object.prototype.hasOwnProperty.call(result, 'session')
      ) {
        const userId = userName.value
        authChallenge.value?.openModal(
          (result as AuthInfo).challengeName,
          (result as AuthInfo).session,
          userId
        )
      } else {
        if (MiiaFwConfig.config.authorization.expirationTime === Number.MAX_VALUE) {
          // 認証成功(有効期限チェックなし)
        } else {
          // パスワード有効期限チェック
          if ((result as AuthInfo).isExpired) {
            // 【要検討】有効期限切れフラグを有効化(パスワード更新画面表示時にブラウザ更新されたら強制サインアウトするため)
            // @ts-expect-error we don't know what is this...
            passwordUpdate.value?.openModal()
          } else {
            // 認証成功
          }
        }
      }
      closeMyModal()
    } catch (error) {
      console.error(error)
      if (isMiiaFwError(error)) {
        if ([/パスワード/].some(e => error.message.match(e))) {
          alertView.pw.hasError = true
        }
        if ([/ユーザーID/].some(e => error.message.match(e))) {
          alertView.un.hasError = true
        }
        alertMessageLight.value?.addAlertMessage(error.message)
      }
    } finally {
      setLoadingView(false) // 必ずコンポーネント消滅前に実行
    }
  }
}

/**
 * MiiaFwPasswordResetのopenModalにuserNameを渡す。
 */
const openPasswordReset = (): void => {
  closeMyModal()
  passwordReset.value?.openModal(userName.value)
}

/**
 * MiiaFwPasswordInitializeのopenModalにargUserNameを渡す。
 * @param argUserName ユーザIDが定義済みのユーザIDならば引数に入れてモーダルに表示する
 */
const openPasswordInitialize = (argUserName: string): void => {
  if (argUserName !== 'undefined' && argUserName.length > 0) userName.value = argUserName
  passwordInitialize.value?.openModal(argUserName)
}

/**
 * 現在のモーダルを閉じ、MiiaFwPasswordUpdateのopenModalを実行。
 */
const openPasswordUpdate = (): void => {
  closeMyModal()
  // @ts-expect-error we don't know what is this...
  passwordUpdate.value?.openModal()
}

defineExpose({
  // data
  modalTop,
  myModal,
  userName,
  password,
  alertView,

  // methods
  openModal,
  openMyModal,
  closeMyModal,
  closeModal,
  firstTab,
  lastTab,
  doSubmit,
  openPasswordReset,
  openPasswordInitialize,
  openPasswordUpdate,

  // refs
  passwordInput
})
</script>

<template>
  <div v-if="modalTop">
    <!-- @closeを指定すると枠外クリック時にモーダルウィンドウを閉じる。 -->
    <!-- <MiiaFwAuthModal @close="closeModal" v-if="modal"> -->
    <MiiaFwAuthModal v-if="myModal" @push-esc="closeModal" @click-close="closeModal">
      <!-- default スロットコンテンツ -->
      <h4 style="font-size: 1.6rem; font-weight: 500">サインイン</h4>
      <div style="padding: 30px 10px 30px 10px; margin-bottom: 10px; border: 1px solid #bcbcbc">
        <div
          class="form-group"
          style="text-align: left"
          v-bind:class="{ 'has-error': alertView.un.hasError }"
        >
          <label class="control-label">
            ユーザーID
            <span v-if="alertView.un.alertMessage.length > 0" class="ff-alert-msg">
              <font-awesome-icon icon="exclamation-circle" />{{ alertView.un.alertMessage }}
            </span>
          </label>
          <input
            ref="userNameInput"
            v-model="userName"
            class="form-control ff-frm-ctrl"
            tabindex="1"
            v-focus
            @keyup.enter="passwordInput.focus()"
            @keydown.shift.tab="firstTab"
          />
        </div>
        <div
          class="form-group"
          la
          style="text-align: left; margin-bottom: 5px"
          v-bind:class="{ 'has-error': alertView.pw.hasError }"
        >
          <label class="control-label">
            パスワード
            <span v-if="alertView.pw.alertMessage.length > 0" class="ff-alert-msg">
              <font-awesome-icon icon="exclamation-circle" />{{ alertView.pw.alertMessage }}
            </span>
          </label>
          <input
            type="password"
            ref="passwordInput"
            v-model="password"
            class="form-control ff-frm-ctrl"
            @keyup.enter="doSubmit"
            tabindex="2"
          />
        </div>
        <p style="text-align: left">
          &gt;
          <a
            href="#!"
            ref="passwordResetLink"
            class="ff-btn-link"
            style="font-size: 0.9rem"
            tabindex="5"
            @click="openPasswordReset"
            @keyup.enter="openPasswordReset"
            @keydown.tab="lastTab"
          >
            パスワードを忘れた場合</a
          >
        </p>
        <div style="text-align: right">
          <button class="btn ff-btn-primary" tabindex="3" @click="doSubmit">
            &nbsp;サインイン
          </button>
        </div>
      </div>
      <MiiaFwAlertMessageLight ref="alertMessageLight"></MiiaFwAlertMessageLight>
      <!-- /default -->
      <!-- footer スロットコンテンツ -->
    </MiiaFwAuthModal>
    <MiiaFwPasswordReset
      ref="passwordReset"
      @open-signin="openMyModal"
      @close-top="closeModal"
      @open-password-initialize="openPasswordInitialize"
    ></MiiaFwPasswordReset>
    <MiiaFwPasswordInitialize
      ref="passwordInitialize"
      @open-signin="openMyModal"
      @close-top="closeModal"
      @open-password-reset="openPasswordReset"
    ></MiiaFwPasswordInitialize>
    <MiiaFwPasswordUpdate ref="passwordUpdate" @close-top="closeModal"></MiiaFwPasswordUpdate>
    <MiiaFwAuthChallenge
      ref="authChallenge"
      @close-top="closeModal"
      @open-password-update="openPasswordUpdate"
    >
    </MiiaFwAuthChallenge>
  </div>
</template>

<style lang="scss" scoped></style>
